import { defineStore } from 'pinia'

import type { UserData } from 'src/models/UserData'
import { UserDataService } from '@services/UserDataService'
import { TokenService } from '@services/TokenService'
import api from 'src/api'
import { useAppStore } from './app'
import { useBookmarkStore } from './bookmarkStore'
import {
  AvailableLanguage,
  getLanguage
} from 'src/helpers/language/AvailableLanguages'
import { AvailableUnits } from 'src/helpers/units/AvailableUnits'
import { socket } from '@services/SocketIOService'
import { DatadogService } from '@services/DatadogService'
import type { CallbackTypes } from 'vue3-google-login'
import { LoginResponse } from '@models/api/LoginResponse'

export const useAuthStore = defineStore('auth', {
  actions: {
    loginWithGoogle: async function (
      googleUser: CallbackTypes.CredentialPopupResponse
    ) {
      try {
        const response = await api.auth.loginWithGoogle(googleUser)
        const body = response.data
        const token = body.TOKEN
        const tokenData = TokenService.getTokenData(token)!
        const layerGroups = response.data.LAYERGROUPS || []
        const userdata: UserData = {
          idUsuario: body.ID_USUARIO,
          nombre: tokenData?.user.NOMBRE,
          apellido: tokenData?.user.APELLIDO,
          email: tokenData?.user.EMAIL,
          permisos: tokenData.user.PERMISOS,
          idRol: tokenData?.user.ID_ROL,
          organization: tokenData.user.ORGANIZATION,
          layerGroups: layerGroups,
          availableLanguages: body.AVAILABLE_LANGUAGES.map(
            (lang: AvailableLanguage) => getLanguage(lang)
          ),
          reportConfig: body.REPORT_CONFIG
        }
        this.SET_USER_DATA(userdata)
        DatadogService.setUserData()
        useAppStore().SET_LANGUAGE(body.IDIOMA || AvailableLanguage.ES)
        useAppStore().SET_UNITS(body.UNIDAD || AvailableUnits.METRIC)
      } catch (err) {
        console.error('Error login', err)
        throw err
      }
    },

    performLogin(email: string, body: LoginResponse) {
      const token = body.TOKEN

      const tokenData = TokenService.getTokenData(token)!

      const layerGroups = body.LAYERGROUPS || []
      const userdata: UserData = {
        idUsuario: body.ID_USUARIO,
        nombre: tokenData?.user.NOMBRE,
        apellido: tokenData?.user.APELLIDO,
        idRol: tokenData?.user.ID_ROL,
        email: email,
        permisos: tokenData.user.PERMISOS,
        organization: tokenData.user.ORGANIZATION,
        layerGroups: layerGroups,
        availableLanguages: body.AVAILABLE_LANGUAGES.map((lang) =>
          getLanguage(lang)
        ),
        reportConfig: body.REPORT_CONFIG
      }

      this.SET_USER_DATA(userdata)
      DatadogService.setUserData()
      useAppStore().SET_LANGUAGE(body.IDIOMA || AvailableLanguage.ES)
      useAppStore().SET_UNITS(body.UNIDAD || AvailableUnits.METRIC)
    },

    async logout(tokenInvalid = false) {
      await api.auth.logout()

      this.CLEAR_USER_DATA()
      useBookmarkStore().clearBookmarks()
      socket.disconnect()

      if (!tokenInvalid) {
        this.router.push('/login')
      } else {
        this.router
          ?.push({ path: 'login', query: { reason: 'token_invalid' } })
          .catch((e) => {
            console.error('Error redirecting to login', e)
          })
      }
    },

    SET_USER_DATA(userData: UserData) {
      UserDataService.saveUserData(userData)
    },
    CLEAR_USER_DATA() {
      // this.userData = undefined
      UserDataService.clearUserData()
    }
  }
})
